import { Injectable } from '@angular/core';
import { DateService } from './date.service';
import { EditingManagerService } from './editing-manager.service';
import { PerformanceMetric } from '../interfaces/statistics.interface';

/**
 * StatisticsFormatterService
 * 
 * Statisztikai adatok formázásáért és megjelenítéséért felelős service.
 * Kezeli az értékek, százalékok formázását és a CSS osztályok meghatározását.
 */
@Injectable()
export class StatisticsFormatterService {

  constructor(
    private dateService: DateService,
    private editingManager: EditingManagerService
  ) {}

  /**
   * Értékek formázása (munkanapokon)
   */
  formatValue(value: number, dayIndex?: number, year?: number, monthIndex?: number): string {
    // Ha hétvége, ne jelenítsen meg semmit
    if (dayIndex !== undefined && year !== undefined && monthIndex !== undefined) {
      if (this.dateService.isWeekend(dayIndex, year, monthIndex)) {
        return '';
      }
    }
    return (value || 0).toString();
  }

  /**
   * Százalékok formázása (munkanapokon)
   */
  formatPercentage(value: number, dayIndex?: number, year?: number, monthIndex?: number): string {
    // Ha hétvége, ne jelenítsen meg semmit
    if (dayIndex !== undefined && year !== undefined && monthIndex !== undefined) {
      if (this.dateService.isWeekend(dayIndex, year, monthIndex)) {
        return '';
      }
    }
    return value > 0 ? `${value}%` : '0%';
  }

  /**
   * CSS osztályok meghatározása cellához
   */
  getCellClass(rowType: string, dayIndex: number, value: number, year: number, monthIndex: number): string {
    const classes = ['cell'];
    
    // Valódi hétvége ellenőrzés (szombat és vasárnap)
    const dayOfWeek = new Date(year, monthIndex, dayIndex + 1).getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) { // 0 = vasárnap, 6 = szombat
      classes.push('weekend');
    }

    const currentEditingWeek = this.editingManager.currentEditingWeek;
    const currentEditingDayIndices = this.editingManager.currentEditingDayIndices;
    
    // Add editing-week class if this cell is in the currently editing week
    if (currentEditingWeek && currentEditingDayIndices.includes(dayIndex)) {
      const isCurrentMetricAndRowType = currentEditingWeek.rowType === rowType;
      
      if (isCurrentMetricAndRowType) {
        classes.push('editing-week');
      }
    }

    return classes.join(' ');
  }

  /**
   * Cell class oszlop jegyzet figyelembevételével
   */
  getCellClassWithNote(
    rowType: string, 
    dayIndex: number, 
    value: number, 
    year: number, 
    monthIndex: number,
    hasNote: boolean,
    noteColor: 'orange' | 'red' = 'orange'
  ): string {
    let baseClass = this.getCellClass(rowType, dayIndex, value, year, monthIndex);
    
    if (hasNote) {
      const colorClass = noteColor === 'red' ? 'column-with-note-red' : 'column-with-note';
      baseClass = baseClass ? baseClass + ' ' + colorClass : colorClass;
    }
    
    return baseClass;
  }

  /**
   * Százalékok újraszámítása egy metrikához
   */
  updatePercentages(metric: PerformanceMetric, daysInMonth: number): void {
    if (metric.percentageValues.length !== daysInMonth) {
      metric.percentageValues = new Array(daysInMonth).fill(0);
    }
    
    for (let i = 0; i < daysInMonth; i++) {
      const actual = metric.actualValues[i] || 0;
      const plan = metric.planValues[i] || 0;
      metric.percentageValues[i] = plan > 0 ? Math.round((actual / plan) * 100) : 0;
    }
  }

  /**
   * Összes metrika százalékainak újraszámítása
   */
  updateAllPercentages(metrics: PerformanceMetric[], daysInMonth: number): void {
    metrics.forEach(metric => this.updatePercentages(metric, daysInMonth));
  }

  /**
   * Biztosítja, hogy minden metric tömb a helyes hosszúságú legyen (daysInMonth)
   */
  ensureCorrectArrayLengths(metrics: PerformanceMetric[], daysInMonth: number): void {
    metrics.forEach((metric, index) => {
      if (metric.actualValues.length !== daysInMonth) {
        // Kiegészítés vagy levágás a helyes hosszúságra
        while (metric.actualValues.length < daysInMonth) {
          metric.actualValues.push(0);
        }
        if (metric.actualValues.length > daysInMonth) {
          metric.actualValues.splice(daysInMonth);
        }
      }
      
      if (metric.planValues.length !== daysInMonth) {
        while (metric.planValues.length < daysInMonth) {
          metric.planValues.push(0);
        }
        if (metric.planValues.length > daysInMonth) {
          metric.planValues.splice(daysInMonth);
        }
      }
      
      if (metric.percentageValues.length !== daysInMonth) {
        while (metric.percentageValues.length < daysInMonth) {
          metric.percentageValues.push(0);
        }
        if (metric.percentageValues.length > daysInMonth) {
          metric.percentageValues.splice(daysInMonth);
        }
      }

      // Jegyzetek tömb is ellenőrzendő
      if (metric.notes) {
        if (metric.notes.length !== daysInMonth) {
          while (metric.notes.length < daysInMonth) {
            metric.notes.push(null);
          }
          if (metric.notes.length > daysInMonth) {
            metric.notes.splice(daysInMonth);
          }
        }
      }
    });
  }

  /**
   * Actual értékek összege
   */
  getActualSum(metric: PerformanceMetric): number {
    return metric.actualValues.reduce((sum, value) => sum + (value || 0), 0);
  }

  /**
   * Plan értékek összege
   */
  getPlanSum(metric: PerformanceMetric): number {
    return metric.planValues.reduce((sum, value) => sum + (value || 0), 0);
  }

  /**
   * Összesített százalék kiszámítása: összes Actual / összes Plan * 100
   */
  getAveragePercentage(metric: PerformanceMetric): number {
    const totalPlan = this.getPlanSum(metric);
    if (totalPlan === 0) return 0;
    const totalActual = this.getActualSum(metric);
    return Math.round((totalActual / totalPlan) * 100);
  }
}